import {DateTime} from 'luxon';
import * as React from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {niceTimeTicks, spanDateFormatter} from 'src/lib/chartDateAxis';
import {
  type GroupMeta,
  formatWholeDollars,
  metaFor,
} from 'src/NetWorthView/netWorthFormat';
import type {NetWorthRole} from '@transactions/graphql';
import type {NetWorthChartPoint} from 'src/NetWorthView/netWorthModel';

type ChartRow = {
  timestamp: number;
  total: number;
} & Partial<Record<NetWorthRole, number>>;

type Series = {
  key: string;
  label: string;
  color: string;
  valueOf: (row: ChartRow) => number | undefined;
};

/** How many horizontal gridlines the value axis aims for. */
const TICK_COUNT = 9;

/** The groups drawn, in tooltip order. */
const seriesFor = (roles: NetWorthRole[], meta: Map<NetWorthRole, GroupMeta>): Series[] =>
  roles.map((role) => ({
    key: role,
    label: metaFor(meta, role).label,
    color: metaFor(meta, role).color,
    valueOf: (row: ChartRow) => row[role],
  }));

/**
 * The lowest and highest value drawn: stacked by sign, positives pile up
 * from zero and negatives down.
 */
const extentOf = (rows: ChartRow[], roles: NetWorthRole[]): [number, number] => {
  let low = Infinity;
  let high = -Infinity;
  for (const row of rows) {
    let up = 0;
    let down = 0;
    for (const role of roles) {
      const value = row[role] ?? 0;
      if (value > 0) up += value;
      else down += value;
    }
    low = Math.min(low, down);
    high = Math.max(high, up);
  }
  return Number.isFinite(low) ? [low, high] : [0, 0];
};

/**
 * The round step (1, 2, 2.5 or 5 × a power of ten) whose tick count over
 * `range` comes closest to `count`.
 */
const niceStep = (range: number, count: number): number => {
  const magnitude = 10 ** Math.floor(Math.log10(range / count));
  const candidates = [0.5, 1, 2, 2.5, 5, 10].map((nice) => nice * magnitude);
  return candidates.reduce((best, step) =>
    Math.abs(range / step - count) < Math.abs(range / best - count) ? step : best,
  );
};

/**
 * The value axis: fitted to what's drawn, with a little headroom, rather
 * than rounded out to wide ticks — a net worth that has never been
 * negative gets no negative band, and one with small debts gets only as
 * much room below zero as they take. Gridlines at round values inside it.
 */
const valueAxis = ([low, high]: [number, number]): {
  domain: [number, number];
  ticks: number[];
} => {
  const span = high - low || Math.abs(high) || 100;
  const pad = span * 0.04;
  const domainLow = low >= 0 ? Math.max(0, low - pad) : low - pad;
  const domainHigh = high + pad;
  const step = niceStep(domainHigh - domainLow, TICK_COUNT);
  // By index, so no floating-point drift accumulates; `+ 0` turns the -0
  // that rounding up from below zero gives into 0 (never "-$0").
  const first = Math.ceil(domainLow / step);
  const ticks: number[] = [];
  for (let index = first; index * step <= domainHigh; index++) {
    ticks.push(index * step + 0);
  }
  return {domain: [domainLow, domainHigh], ticks};
};

type TooltipProps = {
  active?: boolean;
  payload?: Array<{payload: ChartRow}>;
  label?: number;
  revealed: boolean;
  series: Series[];
};

const ChartTooltip = ({active, payload, label, revealed, series}: TooltipProps) => {
  if (!active || !payload?.length || label === undefined) return null;
  const row = payload[0].payload;
  return (
    <div className="bg-card border-border rounded-lg border px-3 py-2 shadow-md">
      <p className="text-muted-foreground text-xs">
        {/* UTC to match the midnight-UTC points and the (UTC) axis labels. */}
        {DateTime.fromMillis(label, {zone: 'utc'}).toFormat('MMM d, yyyy')}
      </p>
      {revealed && (
        <div className="mt-1 flex flex-col gap-0.5">
          {series.map((entry) => {
            const value = entry.valueOf(row);
            if (value == null) return null;
            return (
              <div
                key={entry.key}
                className="flex items-center justify-between gap-4 text-xs"
              >
                <span className="flex items-center gap-1.5">
                  <span
                    className="size-2 rounded-full"
                    style={{backgroundColor: entry.color}}
                  />
                  {entry.label}
                </span>
                <span className="text-foreground font-medium tabular-nums">
                  {formatWholeDollars(value)}
                </span>
              </div>
            );
          })}
          {series.length > 1 && (
            <div className="border-border mt-0.5 flex items-center justify-between gap-4 border-t pt-0.5 text-xs">
              <span>Total shown</span>
              <span className="text-foreground font-medium tabular-nums">
                {formatWholeDollars(row.total)}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const compactDollars = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

/**
 * Net worth over time, each group stacked: positive groups up, negative
 * ones (credit cards) down. Totals hold until some account's next
 * observation changes them, so every series steps rather than sloping
 * between observations. Hidden, the shapes still draw and the axis keeps
 * its place — its labels masked — so revealing never shifts the chart.
 * The group chips above it are its legend.
 */
export const NetWorthChart = ({
  points,
  roles,
  meta,
  revealed,
}: {
  points: NetWorthChartPoint[];
  /** The shown groups, in display order. */
  roles: NetWorthRole[];
  meta: Map<NetWorthRole, GroupMeta>;
  revealed: boolean;
}) => {
  const data = React.useMemo(
    (): ChartRow[] =>
      points.map((point) => ({
        timestamp: DateTime.fromISO(point.date, {zone: 'utc'}).toMillis(),
        total: point.total,
        ...point.byRole,
      })),
    [points],
  );
  const series = seriesFor(roles, meta);
  const axis = React.useMemo(() => valueAxis(extentOf(data, roles)), [data, roles]);
  const firstTs = data[0]?.timestamp ?? 0;
  const lastTs = data[data.length - 1]?.timestamp ?? firstTs;

  return (
    <div className="h-72 sm:h-96">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={data}
          stackOffset="sign"
          margin={{top: 8, right: 8, left: 0, bottom: 0}}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            className="stroke-border"
          />
          <XAxis
            dataKey="timestamp"
            scale="time"
            type="number"
            domain={['dataMin', 'dataMax']}
            ticks={niceTimeTicks(firstTs, lastTs)}
            tickFormatter={spanDateFormatter(firstTs, lastTs)}
            tick={{fontSize: 11}}
            className="text-muted-foreground"
          />
          <YAxis
            type="number"
            domain={axis.domain}
            ticks={axis.ticks}
            interval={0}
            tickFormatter={(cents: number) =>
              revealed ? compactDollars.format(cents / 100) : '•••'
            }
            tick={{fontSize: 11}}
            width={56}
            className="text-muted-foreground"
          />
          <Tooltip content={<ChartTooltip revealed={revealed} series={series} />} />
          {roles.map((role) => (
            <Area
              key={role}
              type="stepAfter"
              dataKey={role}
              stackId="roles"
              stroke={metaFor(meta, role).color}
              strokeWidth={1}
              fill={metaFor(meta, role).color}
              fillOpacity={0.85}
              dot={false}
              isAnimationActive={false}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

/** The chart's footprint while loading: same height, so nothing jumps. */
export const NetWorthChartSkeleton = () => (
  <div className="bg-muted/60 h-72 animate-pulse rounded-lg sm:h-96" />
);
